import React, { useCallback, useMemo, useState } from 'react'
import { Loading } from 'components/loading'
import { HotsiteHeader } from 'domain/feature/hotsite/Header'
import { AvailabilityCalendarModal } from 'domain/feature/public/meeting-room-reservation/AvailabilityCalendarModal'
import { CancelReservationModal } from 'domain/feature/public/meeting-room-reservation/CancelReservationModal'
import { cancelPublicReservation } from 'domain/feature/public/meeting-room-reservation/api/cancelRequest'
import { SpaceImageGalleryModal } from 'domain/feature/public/meeting-room-reservation/SpaceImageGalleryModal'
import { ReservationSuccessModal } from 'domain/feature/public/meeting-room-reservation/ReservationSuccessModal'
import { ReservationForm, requestReservationFormSubmit } from 'domain/feature/public/meeting-room-reservation/ReservationForm'
import { getPublicMeetingRoomRules } from 'domain/feature/public/meeting-room-reservation/reservationRules'
import { canSelectRoom } from 'domain/feature/public/meeting-room-reservation/purposeRules'
import { usePublicMeetingRooms } from 'domain/feature/public/meeting-room-reservation/hooks/usePublicMeetingRooms'
import { usePublicMeetingRoomSettings } from 'domain/feature/public/meeting-room-reservation/hooks/usePublicMeetingRoomSettings'
import { IPublicMeetingRoom } from 'domain/feature/public/meeting-room-reservation/types'
import { SpaceCard } from 'domain/feature/public/meeting-room-reservation/SpaceCard'
import { trans } from 'sharedKernel/i18n'
import {
  AvailabilityButton,
  AvailabilityButtonIcon,
  CancelBanner,
  CancelBannerContent,
  CancelBannerDescription,
  CancelBannerTitle,
  CancelReservationButton,
  EmptyStateMessage,
  EmptyStateSection,
  InfoBox,
  InfoList,
  MainContent,
  NavButtonBack,
  NavButtonPrimary,
  NavigationFooter,
  PageTitle,
  PageWrapper,
  RetryButton,
  SpaceGrid,
} from './style'

const HUB_TEIA_URL = 'https://hubteia.com.br/'

type PageStep = 'selection' | 'form'

export const PublicMeetingRoomReservationPage = (): JSX.Element => {
  const [step, setStep] = useState<PageStep>('selection')
  const [selectedRoomIds, setSelectedRoomIds] = useState<string[]>([])
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [isAvailabilityOpen, setIsAvailabilityOpen] = useState(false)
  const [galleryRoom, setGalleryRoom] = useState<IPublicMeetingRoom | null>(null)
  const [successRequestNumber, setSuccessRequestNumber] = useState<string | null>(null)

  const { rooms: availableRooms, isLoading, isError, refetch } = usePublicMeetingRooms()
  const { settings, isLoading: isSettingsLoading } = usePublicMeetingRoomSettings()

  const selectedRooms = useMemo(
    () => availableRooms.filter(room => selectedRoomIds.includes(room.id)),
    [availableRooms, selectedRoomIds]
  )

  const reservationRules = useMemo(
    () => getPublicMeetingRoomRules(settings, selectedRooms),
    [settings, selectedRooms]
  )

  const hasAvailableRooms = availableRooms.length > 0
  const hasSelection = selectedRoomIds.length > 0
  const canGoToForm = hasSelection && !isSettingsLoading

  const labels = {
    pageTitle: trans('meetingRoom.public.pageTitle'),
    seeAvailability: trans('meetingRoom.public.seeAvailability'),
    emptyState: trans('meetingRoom.public.emptyState'),
    loadError: trans('meetingRoom.public.loadError'),
    retry: trans('meetingRoom.public.retry'),
    cancelBannerTitle: trans('meetingRoom.public.cancelBannerTitle'),
    cancelBannerDescription: trans('meetingRoom.public.cancelBannerDescription'),
    cancelReservation: trans('meetingRoom.public.cancelReservation'),
    next: trans('meetingRoom.public.next'),
    back: trans('meetingRoom.public.form.back'),
    submit: trans('meetingRoom.public.form.submit'),
  }

  const handleToggleRoom = useCallback((roomId: string): void => {
    if (isSettingsLoading || settings == null) return

    setSelectedRoomIds(prev => {
      if (prev.includes(roomId)) {
        return prev.filter(id => id !== roomId)
      }

      const currentlySelected = availableRooms.filter(room => prev.includes(room.id))
      const roomToAdd = availableRooms.find(room => room.id === roomId)
      if (roomToAdd == null) return prev
      if (!canSelectRoom(roomToAdd, currentlySelected, settings)) return prev

      return [...prev, roomId]
    })
  }, [availableRooms, settings, isSettingsLoading])

  const handleNext = (): void => {
    if (!canGoToForm) return
    setStep('form')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleBack = (): void => {
    setStep('selection')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleFormSuccess = (requestNumber: string): void => {
    setSuccessRequestNumber(requestNumber)
    setStep('selection')
    setSelectedRoomIds([])
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleCloseSuccessModal = (): void => {
    setSuccessRequestNumber(null)
  }

  const handleOpenCancelModal = (): void => {
    setIsCancelModalOpen(true)
  }

  const handleCloseCancelModal = (): void => {
    setIsCancelModalOpen(false)
  }

  const handleOpenAvailability = (): void => {
    setIsAvailabilityOpen(true)
  }

  const handleCloseAvailability = (): void => {
    setIsAvailabilityOpen(false)
  }

  const handleOpenGallery = useCallback((room: IPublicMeetingRoom): void => {
    if (room.galleryImages.length === 0) return
    setGalleryRoom(room)
  }, [])

  const handleCloseGallery = (): void => {
    setGalleryRoom(null)
  }

  const handleRetry = (): void => {
    void refetch()
  }

  return (
    <PageWrapper>
      <HotsiteHeader logoHref={HUB_TEIA_URL} />

      <MainContent>
        <PageTitle>{labels.pageTitle}</PageTitle>

        {step === 'selection' && (
          <>
            {!isLoading && !isError && hasAvailableRooms && (
              <InfoBox>
                <InfoList>
                  {reservationRules.infoItems.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </InfoList>
              </InfoBox>
            )}

            <AvailabilityButton
              type="button"
              $isActive={!isLoading && !isError && hasAvailableRooms}
              disabled={isLoading || isError || !hasAvailableRooms}
              aria-disabled={isLoading || isError || !hasAvailableRooms}
              onClick={handleOpenAvailability}
            >
              <AvailabilityButtonIcon
                aria-hidden
                $isActive={!isLoading && !isError && hasAvailableRooms}
              />
              {labels.seeAvailability}
            </AvailabilityButton>

            {isLoading ? (
              <Loading />
            ) : isError ? (
              <EmptyStateSection aria-live="polite">
                <EmptyStateMessage>{labels.loadError}</EmptyStateMessage>
                <RetryButton type="button" onClick={handleRetry}>
                  {labels.retry}
                </RetryButton>
              </EmptyStateSection>
            ) : hasAvailableRooms ? (
              <SpaceGrid>
                {availableRooms.map(room => {
                  const isSelected = selectedRoomIds.includes(room.id)
                  const isSelectionDisabled =
                    isSettingsLoading ||
                    settings == null ||
                    !canSelectRoom(room, selectedRooms, settings)

                  return (
                    <SpaceCard
                      key={room.id}
                      room={room}
                      isSelected={isSelected}
                      isSelectionDisabled={isSelectionDisabled}
                      onToggle={handleToggleRoom}
                      onImageClick={handleOpenGallery}
                    />
                  )
                })}
              </SpaceGrid>
            ) : (
              <EmptyStateSection aria-live="polite">
                <EmptyStateMessage>{labels.emptyState}</EmptyStateMessage>
              </EmptyStateSection>
            )}

            <CancelBanner>
              <CancelBannerContent>
                <CancelBannerTitle>{labels.cancelBannerTitle}</CancelBannerTitle>
                <CancelBannerDescription>{labels.cancelBannerDescription}</CancelBannerDescription>
              </CancelBannerContent>
              <CancelReservationButton type="button" onClick={handleOpenCancelModal}>
                {labels.cancelReservation}
              </CancelReservationButton>
            </CancelBanner>
          </>
        )}

        {step === 'form' && (
          <ReservationForm
            selectedRooms={selectedRooms}
            onBack={handleBack}
            onSuccess={handleFormSuccess}
          />
        )}
      </MainContent>

      <NavigationFooter>
        {step === 'selection' ? (
          <>
            <span />
            <NavButtonPrimary
              type="button"
              disabled={!canGoToForm}
              onClick={handleNext}
            >
              {labels.next}
            </NavButtonPrimary>
          </>
        ) : (
          <>
            <NavButtonBack type="button" onClick={handleBack}>
              {labels.back}
            </NavButtonBack>
            <NavButtonPrimary
              type="button"
              onClick={requestReservationFormSubmit}
            >
              {labels.submit}
            </NavButtonPrimary>
          </>
        )}
      </NavigationFooter>

      <CancelReservationModal
        isOpen={isCancelModalOpen}
        onClose={handleCloseCancelModal}
        onConfirm={cancelPublicReservation}
      />

      <AvailabilityCalendarModal
        isOpen={isAvailabilityOpen}
        onClose={handleCloseAvailability}
        rooms={availableRooms}
        settings={settings}
      />

      <ReservationSuccessModal
        isOpen={successRequestNumber != null}
        requestNumber={successRequestNumber ?? ''}
        onClose={handleCloseSuccessModal}
      />

      {galleryRoom != null && (
        <SpaceImageGalleryModal
          isOpen
          roomId={galleryRoom.id}
          roomName={galleryRoom.name}
          description={galleryRoom.description}
          equipment={galleryRoom.equipment}
          images={galleryRoom.galleryImages}
          onClose={handleCloseGallery}
        />
      )}
    </PageWrapper>
  )
}
